import LoadingButton from '@mui/lab/LoadingButton';
import Autocomplete from '@mui/material/Autocomplete';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { ChangeEvent, FocusEvent, FormEvent, useEffect, useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import { DotLoader } from '~components/DotLoader';
import SectionCard from '~components/SectionCard';
import useDebounce from '~hooks/useDebounce';
import { moveLeadsListCampaign } from '~pages/CampaignManagement/api';
import useCampaignSearch from '~pages/CampaignManagement/CampaignList/useCampaignSearch';
import { CampaignFilterType } from '~pages/CampaignManagement/CampaignList/useCampaignSearch/domain';
import { CampaignListItem } from '~pages/CampaignManagement/domain';
import { useNotification } from '~providers/NotificationProvider';
import Routes from '~providers/RouteProvider/Routes';
import { APIError, UnsupportedStructureError } from '~services/Errors';

type Props = {
  campaignId: number;
  listId: number;
};

export const MoveLeadsListForm = ({ campaignId, listId }: Props) => {
  const navigate = useNavigate();
  const { pushNotification } = useNotification();
  const [campaignSearch, setCampaignSearch] = useState<string>('');
  const debouncedCampaignSearch = useDebounce(campaignSearch, 500);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [moveToCampaignId, setMoveToCampaignId] = useState<number | undefined>(undefined);
  const {
    loading: campaignFetching,
    error: campaignFetchError,
    list: campaigns,
    intersectionObserverRef: lastCampaignDataElement,
  } = useCampaignSearch(debouncedCampaignSearch, { filters: [CampaignFilterType.Active, CampaignFilterType.Paused] });

  useEffect(() => {
    if (!campaignFetching && !campaignFetching) {
      // set initial value to current campaign
      setMoveToCampaignId(+campaignId);
    }
  }, [campaignFetching, campaignFetchError]);

  const onMoveToCampaignChange = (data: CampaignListItem | null) => {
    setMoveToCampaignId(data?.id || undefined);
  };

  const onMoveToCampaignSubmit = async (e?: FormEvent<HTMLFormElement>) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }

    if (moveToCampaignId === undefined || moveToCampaignId === +campaignId) {
      return;
    }

    setSubmitting(true);

    try {
      await moveLeadsListCampaign(moveToCampaignId, +listId);
    } catch (e) {
      let msg = (e as APIError | UnsupportedStructureError).message;
      if ((e as APIError).status === 400) {
        msg = 'Unable to move only list in campaign or default list. Create a new list first.';
      }
      pushNotification('error', msg);
      return;
    } finally {
      setSubmitting(false);
    }

    pushNotification('success', 'You have successfully moved the list.');
    navigate(`${generatePath(Routes.viewCampaign.path, { campaignId: moveToCampaignId.toString() })}?show=LeadLists`);
  };

  const onCampaignSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setCampaignSearch(e.target.value);
  };

  const onCampaignSearchBlur = (e: FocusEvent<HTMLInputElement>) => {
    setCampaignSearch('');
  };

  const campaignNoOptionsText = useMemo(() => {
    if (campaignFetching) {
      return <DotLoader align='center' />;
    }

    if (campaignFetchError) {
      return (
        <Typography variant='body2' align='center' color='textSecondary'>
          Failed to load campaigns
        </Typography>
      );
    }

    return undefined;
  }, [campaignFetching, campaignFetchError]);

  const filteredCampaignsList = useMemo(() => {
    if (!moveToCampaignId) {
      return campaigns;
    }

    return campaigns.filter((listItem) => {
      return listItem.id !== moveToCampaignId;
    });
  }, [campaigns, moveToCampaignId]);

  return (
    <SectionCard title='Move List to Different Campaign'>
      <form onSubmit={onMoveToCampaignSubmit} noValidate>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Autocomplete
              id='moveToCampaign'
              fullWidth
              onChange={(e, data) => {
                onMoveToCampaignChange(data);
              }}
              options={filteredCampaignsList}
              noOptionsText={campaignNoOptionsText}
              disabled={submitting}
              getOptionLabel={(option) => option?.name || ''}
              renderOption={(props, option) => (
                <li {...props} ref={lastCampaignDataElement} key={option.id}>
                  {option.name}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Destination campaign'
                  variant='outlined'
                  required={true}
                  onBlur={onCampaignSearchBlur}
                  onChange={onCampaignSearchChange}
                />
              )}
            />
          </Grid>

          <Grid sx={{ textAlign: 'right' }} item xs={12} md={6}>
            <LoadingButton
              sx={{ marginLeft: 1 }}
              type='submit'
              variant='contained'
              disableElevation
              loading={submitting}
              disabled={moveToCampaignId === undefined || moveToCampaignId === +campaignId}
              color='primary'>
              Move List
            </LoadingButton>
          </Grid>
        </Grid>
      </form>
    </SectionCard>
  );
};

export default MoveLeadsListForm;
